import { useMemo } from 'react';
import { useGetTask } from '../api/task-api/task-api';
import { TaskDto, ProjectDto } from '../api/model';
import { useProjectDeliverables } from './useProjectDeliverables';

export const useProjectTasks = (project?: ProjectDto) => {
  // Fetch all tasks
  const { 
    data: allTasksData, 
    isLoading: isTasksLoading, 
    error: tasksError 
  } = useGetTask({
    query: {
      staleTime: 0,
      refetchOnMount: true,
      refetchOnWindowFocus: false,
      retry: 3,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    }
  });
  
  // Fetch deliverables for this project
  const { 
    deliverables: projectDeliverables, 
    isLoading: isDeliverablesLoading 
  } = useProjectDeliverables(project);
  
  const projectTasks = useMemo(() => {
    if (!allTasksData || !project) {
      return [];
    }

    const projectDeliverableIds = new Set<number>();
    if (projectDeliverables) {
      projectDeliverables.forEach(deliverable => {
        if (deliverable.id) {
          projectDeliverableIds.add(deliverable.id);
        }
      });
    }
    
    // Filter tasks
    return allTasksData.filter((task: TaskDto) => {
      if (projectDeliverableIds.size === 0) {
        return false;
      }
      
      return task.deliverObjectId && projectDeliverableIds.has(task.deliverObjectId);
    });
  }, [allTasksData, project, projectDeliverables]);

  return {
    tasks: projectTasks,
    isLoading: isTasksLoading || isDeliverablesLoading,
    error: tasksError,
    hasData: projectTasks.length > 0
  };
};
